/**
 * gallery.ts
 *
 * Prompt selector dropdown, "Show all / Show less" toggle, and zip
 * download spinner for the experiment gallery partial.
 *
 * The gallery HTML is swapped in via HTMX (hx-swap-oob), so we
 * re-initialise listeners on every htmx:afterSettle that touches
 * the gallery container.
 */

function initGallery(): void {
    // ── Prompt selector dropdown ──
    const selector = document.getElementById('prompt-selector');
    const btn = document.getElementById('prompt-selector-btn');
    const btnText = document.getElementById('prompt-selector-btn-text');
    const panel = document.getElementById('prompt-selector-panel');
    const search = document.getElementById('prompt-selector-search') as HTMLInputElement | null;
    const chevron = document.querySelector<SVGElement>('.prompt-selector-chevron');
    const options = document.querySelectorAll<HTMLElement>('.prompt-selector-option');
    const sections = document.querySelectorAll<HTMLElement>('.prompt-section');

    if (selector && btn && panel && !selector.dataset.bound) {
        selector.dataset.bound = '1';

        function setOpen(open: boolean): void {
            panel!.classList.toggle('hidden', !open);
            btn!.setAttribute('aria-expanded', String(open));
            if (chevron) chevron.style.transform = open ? 'rotate(180deg)' : '';
        }

        btn.addEventListener('click', () => {
            const isOpen = !panel.classList.contains('hidden');
            setOpen(!isOpen);
            if (!isOpen && search) {
                search.value = '';
                filterOptions('');
                search.focus();
            }
        });

        // Close on outside click
        document.addEventListener('click', (e) => {
            if (!selector.contains(e.target as Node)) {
                setOpen(false);
            }
        });

        if (search) {
            search.addEventListener('input', () => {
                filterOptions(search.value.toLowerCase());
            });
        }

        function filterOptions(query: string): void {
            options.forEach((opt) => {
                const text = opt.textContent!.toLowerCase();
                opt.style.display = text.indexOf(query) !== -1 ? '' : 'none';
            });
        }

        options.forEach((opt) => {
            opt.addEventListener('click', function () {
                const idx = this.dataset.promptIndex || '';

                // Update selected state
                options.forEach((o) => {
                    o.classList.remove('prompt-selector-option-selected');
                });
                this.classList.add('prompt-selector-option-selected');

                // Update button text
                if (btnText) {
                    btnText.innerHTML = '&ldquo;' + this.textContent!.trim().substring(0, 50) + '&rdquo;';
                }

                // Show/hide sections
                sections.forEach((s) => {
                    s.style.display = s.dataset.promptIndex === idx ? '' : 'none';
                });

                // Close panel
                setOpen(false);
            });
        });
    }

    // ── Download images zip with spinner ──
    const zipBtn = document.getElementById('download-zip-btn') as HTMLAnchorElement | null;
    if (zipBtn && !zipBtn.dataset.bound) {
        zipBtn.dataset.bound = '1';
        zipBtn.addEventListener('click', (e) => {
            e.preventDefault();
            if (zipBtn.classList.contains('disabled')) return;

            const href = zipBtn.href;
            const origHTML = zipBtn.innerHTML;
            zipBtn.innerHTML =
                '<span class="spinner-border spinner-border-sm me-1"></span>Preparing zip\u2026';
            zipBtn.classList.add('disabled');

            fetch(href)
                .then((resp) => {
                    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                    const cd = resp.headers.get('Content-Disposition') || '';
                    const match = cd.match(/filename="(.+)"/);
                    const filename = match ? match[1] : 'images.zip';
                    return resp.blob().then((blob) => ({ blob, filename }));
                })
                .then(({ blob, filename }) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                })
                .catch(() => {
                    alert('Failed to download images. Please try again.');
                })
                .finally(() => {
                    zipBtn.innerHTML = origHTML;
                    zipBtn.classList.remove('disabled');
                });
        });
    }

    // ── Show all / show less toggle ──
    // The button is rendered by the generic _ui-button include (no data
    // attributes), so derive the prompt section from the DOM ancestry rather
    // than a data-prompt-index lookup.
    document.querySelectorAll<HTMLButtonElement>('.show-all-btn').forEach((toggleBtn) => {
        if (toggleBtn.dataset.bound) return;
        toggleBtn.dataset.bound = '1';
        toggleBtn.addEventListener('click', function () {
            const section = this.closest<HTMLElement>('.prompt-section');
            if (!section) return;

            const group1 = section.querySelector('.image-gallery') as HTMLElement | null;
            const byModel = section.querySelector('.all-by-model') as HTMLElement | null;
            if (!byModel) return;

            const showing = byModel.style.display !== 'none';
            if (group1) group1.style.display = showing ? '' : 'none';
            byModel.style.display = showing ? 'none' : '';
            // The label lives in a styled span inside the button.
            const label = this.querySelector('span');
            if (label) label.textContent = showing ? 'Show all' : 'Show less';
        });
    });
}

// Run on initial load
initGallery();

// Re-run after every HTMX settle. The gallery is swapped in out-of-band
// (`hx-swap-oob` on #experiment-gallery) while the polling request's main target
// is #experiment-status, so we can't rely on evt.detail.target being the gallery
// — when the gallery first appears mid-poll the target is the status partial, and
// the old `target === gallery` guard missed it, leaving the prompt dropdown dead
// until a full page reload. initGallery() queries the document globally and is
// idempotent (bindings are guarded by data-bound), so calling it on each settle
// safely binds the gallery whenever it lands, regardless of swap target.
document.body.addEventListener('htmx:afterSettle', (() => {
    initGallery();
}) as EventListener);
